import { __rest, __assign } from 'tslib';
import React, { forwardRef, useRef, useMemo, useState, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import { maskitoTransform } from '@maskito/core';
import { useMaskito } from '@maskito/react';
import { BaseOption } from '../../../../select/esm/shared';
import { isNullable } from '../../../../shared/esm';
import { initCountries, findCountry, filterPhones, createMaskOptions, getClear, getPhoneData, getInitialValueFromCountry } from '../../utils/index.js';
import { CountrySelect } from '../country-select/Component.js';
import '../../consts.js';
import '../../data/country-data.js';
import '@alfalab/icons-glyph/WorldMagnifierMIcon';
import '../flag-icon/component.js';
import 'classnames';
import '../flag-icon/flagSprite.js';
import '../select-field/component.js';
import '@alfalab/hooks';

var styles = {"component":"international-phone-input__component_y7lpn","addons":"international-phone-input__addons_y7lpn"};
require('./index.css');

var BaseInternationalPhoneInput = forwardRef(function (_a, ref) {
    var _b = _a.clearableCountryCode, clearableCountryCodeFromProps = _b === void 0 ? true : _b, value = _a.value, countryProp = _a.country, filterFn = _a.filterFn, onChange = _a.onChange, onCountryChange = _a.onCountryChange, countrySelectProps = _a.countrySelectProps, countries = _a.countries, defaultIso2 = _a.defaultIso2, disabled = _a.disabled, options = _a.options, _c = _a.size, size = _c === void 0 ? 56 : _c, Input = _a.Input, InputAutocomplete = _a.InputAutocomplete, SelectComponent = _a.SelectComponent, view = _a.view, clearProp = _a.clear, openProps = _a.open, defaultOpen = _a.defaultOpen, customCountriesList = _a.customCountriesList, restProps = __rest(_a, ["clearableCountryCode", "value", "country", "filterFn", "onChange", "onCountryChange", "countrySelectProps", "countries", "defaultIso2", "disabled", "options", "size", "Input", "InputAutocomplete", "SelectComponent", "view", "clear", "open", "defaultOpen", "customCountriesList"]);
    var lastCountryRef = useRef(null);
    var countriesData = useMemo(function () { return initCountries(countries, customCountriesList); }, [countries, customCountriesList]);
    var inputRef = useRef(null);
    var inputWrapperRef = useRef(null);
    var _d = useState(defaultOpen), open = _d[0], setOpen = _d[1];
    var _e = useState(countrySelectProps === null || countrySelectProps === void 0 ? void 0 : countrySelectProps.defaultOpen), openCountry = _e[0], setOpenCountry = _e[1];
    var _f = useState(function () {
        return findCountry(countriesData, value, defaultIso2, countryProp);
    }), selectedCountry = _f[0], setSelectedCountry = _f[1];
    var filteredOptions = filterPhones(value, options, filterFn);
    var country = countryProp !== null && countryProp !== void 0 ? countryProp : selectedCountry;
    var handleCountryChange = function (nextCountry) {
        if (countryProp === undefined)
            setSelectedCountry(nextCountry);
        onCountryChange === null || onCountryChange === void 0 ? void 0 : onCountryChange(nextCountry);
    };
    var preserveCountryCode = clearableCountryCodeFromProps === 'preserve';
    var clearableCountryCode = preserveCountryCode || clearableCountryCodeFromProps;
    var maskOptions = useMemo(function () {
        return createMaskOptions(country, clearableCountryCode, preserveCountryCode, lastCountryRef);
    }, [country, clearableCountryCode, preserveCountryCode]);
    var maskRef = useMaskito({ options: maskOptions });
    var changeNumber = function (phone) {
        onChange === null || onChange === void 0 ? void 0 : onChange(phone);
    };
    var updatePhoneData = function (phone) {
        var _a = getPhoneData(phone, countriesData, defaultIso2), nextCountry = _a.nextCountry, nextPhone = _a.nextPhone;
        if (nextCountry !== country) {
            handleCountryChange === null || handleCountryChange === void 0 ? void 0 : handleCountryChange(nextCountry);
        }
        changeNumber(nextPhone);
    };
    var handleSelectCountry = function (_a) {
        var selected = _a.selected;
        var nextCountry = selected === null || selected === void 0 ? void 0 : selected.value;
        handleCountryChange === null || handleCountryChange === void 0 ? void 0 : handleCountryChange(nextCountry);
        if (nextCountry) {
            changeNumber(getInitialValueFromCountry(nextCountry.countryCode));
        }
        requestAnimationFrame(function () { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
    };
    var handleOptionSelect = function (payload) {
        var _a;
        updatePhoneData(maskitoTransform(typeof payload === 'string' ? payload : ((_a = payload.selected) === null || _a === void 0 ? void 0 : _a.key) || '', maskOptions));
    };
    var handleInput = function (_a) {
        var inputValue = _a.target.value;
        updatePhoneData(inputValue);
    };
    var handleClear = function (event) {
        var _a, _b;
        (_b = (_a = restProps.inputProps) === null || _a === void 0 ? void 0 : _a.onClear) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        if (clearableCountryCode) {
            var nextCountry = findCountry(countriesData, '', defaultIso2, countryProp);
            changeNumber('');
            handleCountryChange(nextCountry);
        }
        else {
            changeNumber(country ? getInitialValueFromCountry(country.countryCode) : '');
        }
    };
    useEffect(function () {
        if (value) {
            var newValue = maskitoTransform(value, maskOptions);
            if (value !== newValue) {
                updatePhoneData(newValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, maskOptions]);
    var openPhoneSelect = function (payload) {
        var _a;
        if (openProps === undefined)
            setOpen(payload.open);
        (_a = restProps.onOpen) === null || _a === void 0 ? void 0 : _a.call(restProps, payload);
    };
    var openCountrySelect = function (payload) {
        var _a;
        if ((countrySelectProps === null || countrySelectProps === void 0 ? void 0 : countrySelectProps.open) === undefined)
            setOpenCountry(payload.open);
        (_a = countrySelectProps === null || countrySelectProps === void 0 ? void 0 : countrySelectProps.onOpen) === null || _a === void 0 ? void 0 : _a.call(countrySelectProps, payload);
    };
    var handlePhoneSelectOpen = function (payload) {
        if (payload.open) {
            openCountrySelect({ open: false });
        }
        openPhoneSelect(payload);
    };
    var handleCountrySelectOpen = function (payload) {
        if (payload.open) {
            openPhoneSelect({ open: false });
        }
        openCountrySelect(payload);
    };
    var showPhoneSelect = Boolean(open || openProps);
    var showCountrySelect = Boolean(openCountry || (countrySelectProps === null || countrySelectProps === void 0 ? void 0 : countrySelectProps.open));
    var renderCountrySelect = function (compact) {
        var _a;
        if (compact === void 0) { compact = false; }
        return (React.createElement(CountrySelect, __assign({ dataTestId: restProps === null || restProps === void 0 ? void 0 : restProps.dataTestId }, countrySelectProps, { view: view, SelectComponent: SelectComponent, disabled: disabled || (countrySelectProps === null || countrySelectProps === void 0 ? void 0 : countrySelectProps.disabled), onChange: handleSelectCountry, country: country, countries: compact ? [] : countriesData, fieldWidth: (_a = inputWrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width, onOpen: handleCountrySelectOpen, open: showCountrySelect })));
    };
    var inputProps = __assign({ className: styles.component, ref: mergeRefs([maskRef, ref, inputRef]), wrapperRef: inputWrapperRef, addonsClassName: styles.addons, type: 'tel', clear: getClear(clearProp, clearableCountryCode, value, country === null || country === void 0 ? void 0 : country.countryCode) }, restProps.inputProps);
    useEffect(function () {
        if (!preserveCountryCode || isNullable(country)) {
            return;
        }
        lastCountryRef.current = country;
    }, [country, preserveCountryCode]);
    return Array.isArray(options) ? (React.createElement(InputAutocomplete, __assign({ closeOnSelect: true, Option: BaseOption, size: size }, restProps, { disabled: disabled, options: filteredOptions, value: value, open: showPhoneSelect, onOpen: handlePhoneSelectOpen, onChange: handleOptionSelect, onInput: function (phone) { return updatePhoneData(phone); }, inputProps: __assign(__assign({}, inputProps), { onClear: handleClear, onInput: handleInput, leftAddons: renderCountrySelect(view === 'mobile') }), fieldProps: __assign(__assign(__assign({}, restProps.fieldProps), { className: inputProps.className, addonsClassName: inputProps.addonsClassName }), (view === 'mobile' ? { leftAddons: renderCountrySelect() } : null)) }))) : (React.createElement(Input, __assign({}, restProps, inputProps, { onClear: inputProps.clear ? handleClear : undefined, leftAddons: renderCountrySelect(), size: size, onInput: handleInput, value: value, disabled: disabled })));
});

export { BaseInternationalPhoneInput };
